/**
 * Privacy Policy Page
 *
 * Public page (no authentication required)
 * Static content for legal compliance
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import "./LegalPages.css";

export function PrivacyPage(): JSX.Element {
  return (
    <PublicLayout>
      <div className="legal-page">
      <div className="legal-page__container">
        <h1>Privacy Policy</h1>
        <p className="legal-page__last-updated">Last Updated: August 23, 2026</p>

        <section className="legal-page__section">
          <h2>1. Information We Collect</h2>
          <p>
            We collect information that you provide directly to us, including:
          </p>
          <ul>
            <li>Account information (email, name)</li>
            <li>API keys (encrypted and stored securely)</li>
            <li>Trading activity and positions</li>
            <li>Payment information (for subscriptions)</li>
            <li>Support tickets and communications</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Place automated orders on your connected account</li>
            <li>Process payments and subscriptions</li>
            <li>Send you notifications and updates</li>
            <li>Respond to your support requests</li>
            <li>Improve our Service</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>3. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information:
          </p>
          <ul>
            <li>API keys are encrypted at rest using Fernet encryption</li>
            <li>HTTPS/TLS encryption for all data transmission</li>
            <li>Secure session management with HttpOnly cookies</li>
            <li>Regular security audits and updates</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>4. Data Sharing</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties. We may share your information only:
          </p>
          <ul>
            <li>With your explicit consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and safety</li>
            <li>With service providers who assist in operating our Service (under strict confidentiality agreements)</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>5. Third-Party Service Providers</h2>
          <p>
            We rely on third-party providers to operate the Service. These providers may process your information on our behalf and
            include, but are not limited to:
          </p>
          <ul>
            <li>Exchange platforms (e.g., Bybit) for executing trades via your API keys</li>
            <li>Authentication providers (e.g., Auth0) for account login</li>
            <li>Cloud infrastructure and hosting providers (e.g., DigitalOcean)</li>
            <li>Messaging providers (e.g., Telegram) for delivering notifications</li>
            <li>Payment processors for subscription transactions</li>
          </ul>
          <p>
            Each provider operates under its own privacy policy and data practices. We encourage you to review the privacy policies of
            these third parties.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to provide the Service and fulfill the purposes described
            in this Privacy Policy, or as required by law. Specifically:
          </p>
          <ul>
            <li>Account information is retained while your account remains active</li>
            <li>Trading history is retained to support the Service's reporting and reconciliation features</li>
            <li>Upon account deletion, we delete or anonymize your personal information, except where retention is required by law</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your account and data</li>
            <li>Opt-out of certain communications</li>
            <li>Export your data</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>8. Cookies</h2>
          <p>
            We use cookies to maintain your session and improve your experience. Cookies are HttpOnly and secure in production. You can control cookies through your browser settings.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>9. Children's Privacy</h2>
          <p>
            The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from
            children. If you believe a child has provided us with personal information, please contact us and we will delete it.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>10. Data Breach Notification</h2>
          <p>
            In the event of a data breach that affects your personal information, we will notify affected users and relevant authorities
            as required by applicable law.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us through the Support page.
          </p>
        </section>
      </div>
      </div>
    </PublicLayout>
  );
}

export default PrivacyPage;
